import React from 'react';
import { motion } from 'framer-motion';
import { Shield, User, Sparkles, Zap, Activity, Users, Crown } from 'lucide-react';
import { formatNumber, formatDate } from '../../utils/formatters';

// Polished Glassy amPassport Card Component
const LiquidGlassPassport = ({ userData, currentAvatar, level, levelTitle }) => {
  // Get top 3 point categories
  const pointsData: Record<string, number> = userData.pointsStats || userData.pointsBreakdown || {};
  const topCategories = Object.entries(pointsData)
    .map(([key, points]) => ({
      name: key === 'github' ? 'GitHub' : 
            key === 'x' || key === 'tweet' ? 'X Posts' :
            key === 'ai' || key === 'AIActivity' ? 'AI Activity' :
            key === 'gm' ? 'GM Streaks' :
            key === 'gn' ? 'GN Streaks' :
            key === 'sendit' ? 'SendIt' :
            key === 'role' ? 'Roles' : key.charAt(0).toUpperCase() + key.slice(1),
      points: points || 0,
      color: key === 'github' ? '#9FA6B2' :
            key === 'x' || key === 'tweet' ? '#3EBEFF' :
            key === 'ai' || key === 'AIActivity' ? '#FFD964' :
            key === 'gm' ? '#FFD964' :
            key === 'gn' ? '#3EBEFF' :
            key === 'sendit' ? '#00FF9D' :
            '#00FF9D',
      icon: key === 'github' ? Activity :
            key === 'x' || key === 'tweet' ? Sparkles :
            key === 'ai' || key === 'AIActivity' ? Zap :
            key === 'role' ? Users :
            Activity
    }))
    .filter(cat => cat.points > 0)
    .sort((a, b) => b.points - a.points)
    .slice(0, 3);

  // Neural network nodes for background
  const neuralNodes = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2
  }));

  // Get X profile avatar if available
  const displayAvatar = userData?.xId !== "Not linked" && userData?.xId ? 
    `https://unavatar.io/x/${userData.xId}` : currentAvatar;

  return (
    <motion.div
      className="relative w-full max-w-md mx-auto"
      initial={{ opacity: 0, y: 50, rotateY: -15 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
      style={{ perspective: "1000px" }}
    >
      <div className="relative">
        {/* Enhanced Glow Effect Background */}
        <div className="absolute inset-0 -m-8 opacity-60">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-[#00FF9D]/25 via-[#3EBEFF]/20 to-[#FFD964]/25 blur-3xl rounded-full"
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, 180, 360],
              }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            />
          </div>
        </div>

        {/* Main Passport Card with Enhanced Glassy Effect */}
        <motion.div
          className="relative w-full h-[440px] md:h-[480px] rounded-3xl overflow-hidden"
          style={{
            background: `
              linear-gradient(135deg, 
                rgba(16, 19, 31, 0.95) 0%, 
                rgba(10, 14, 27, 0.98) 30%,
                rgba(20, 24, 36, 0.95) 70%, 
                rgba(16, 19, 31, 0.95) 100%
              )`,
            backdropFilter: "blur(25px)",
            boxShadow: `
              0 0 0 1px rgba(0, 255, 157, 0.15),
              0 25px 50px rgba(0, 0, 0, 0.6),
              0 50px 100px rgba(0, 255, 157, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.1),
              inset 0 0 30px rgba(0, 255, 157, 0.05)
            `,
          }}
          whileHover={{ 
            rotateY: 8, 
            rotateX: 3,
            scale: 1.03,
            transition: { duration: 0.5 }
          }}
        >
          {/* Abstract Neural Network Background */}
          <div className="absolute inset-0 opacity-8">
            <svg className="w-full h-full">
              {/* Neural connections */}
              {neuralNodes.map((node, i) => 
                neuralNodes.slice(i + 1).map((targetNode, j) => {
                  const distance = Math.sqrt(
                    Math.pow(node.x - targetNode.x, 2) + Math.pow(node.y - targetNode.y, 2)
                  );
                  if (distance < 30) {
                    return (
                      <motion.line
                        key={`${i}-${j}`}
                        x1={`${node.x}%`}
                        y1={`${node.y}%`}
                        x2={`${targetNode.x}%`}
                        y2={`${targetNode.y}%`}
                        stroke="rgba(0, 255, 157, 0.3)"
                        strokeWidth="1"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ 
                          pathLength: 1, 
                          opacity: [0.3, 0.6, 0.3] 
                        }}
                        transition={{
                          pathLength: { duration: 2, delay: node.delay },
                          opacity: { duration: 3, repeat: Infinity, delay: node.delay }
                        }}
                      />
                    );
                  }
                  return null;
                })
              )}
              
              {/* Neural nodes */}
              {neuralNodes.map((node, i) => (
                <motion.circle
                  key={i}
                  cx={`${node.x}%`}
                  cy={`${node.y}%`}
                  r="2"
                  fill="#00FF9D"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ 
                    scale: [1, 1.5, 1], 
                    opacity: [0.4, 0.8, 0.4] 
                  }}
                  transition={{
                    duration: node.duration,
                    repeat: Infinity,
                    delay: node.delay
                  }}
                />
              ))}
            </svg>
          </div>

          {/* Enhanced Floating Energy Orbs */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-12 left-12 w-40 h-40 bg-[#00FF9D]/15 rounded-full blur-3xl"
              animate={{
                x: [0, 60, 0],
                y: [0, -40, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute bottom-12 right-12 w-50 h-50 bg-[#3EBEFF]/15 rounded-full blur-3xl"
              animate={{
                x: [0, -40, 0],
                y: [0, 40, 0],
                scale: [1, 1.3, 1],
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            />
            <motion.div
              className="absolute top-1/2 left-1/4 w-30 h-30 bg-[#FFD964]/12 rounded-full blur-2xl"
              animate={{
                x: [0, 30, 0],
                y: [0, -20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            />
          </div>

          {/* Content */}
          <div className="relative z-10 p-6">
            {/* Header with Enhanced Status */}
            <div className="flex items-center justify-between mb-5">
              <motion.div 
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="relative">
                  <div className="w-3 h-3 bg-[#00FF9D] rounded-full animate-pulse shadow-lg shadow-[#00FF9D]/50" />
                  <div className="absolute inset-0 w-3 h-3 bg-[#00FF9D] rounded-full animate-ping" />
                </div>
                <span className="text-[#00FF9D] font-bold text-sm tracking-wider">VERIFIED</span>
              </motion.div>
              
              <motion.div 
                className="text-right"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="text-xs text-[#7C8694] font-medium mb-1">amPassport ID</div>
                <div className="text-[#FFD964] font-mono font-bold text-xs leading-tight">
                  {userData?.processId?.slice(0, 12) || "PROCESSING..."}
                </div>
              </motion.div>
            </div>

            {/* Enhanced Profile Section */}
            <div className="flex items-start gap-4 mb-5">
              {/* Avatar Container with Better Effects */}
              <motion.div
                className="relative"
                whileHover={{ scale: 1.08 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                <div className="relative w-20 h-20 rounded-2xl overflow-hidden">
                  {/* Enhanced Avatar Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00FF9D]/40 to-[#3EBEFF]/40 animate-pulse" />
                  
                  {/* Avatar Image/Icon */}
                  <div className="relative w-full h-full bg-gradient-to-br from-[#1A1F2E] to-[#0A0E1B]">
                    {displayAvatar ? (
                      <img 
                        src={displayAvatar} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center" style={{ display: displayAvatar ? 'none' : 'flex' }}>
                      <User className="w-10 h-10 text-[#7C8694]" />
                    </div>
                  </div>
                </div>
                
                {/* Smaller Level Badge */}
              </motion.div>

              {/* Refined Identity Info */}
              <div className="flex-1 min-w-0">
                <motion.h3
                  className="text-lg font-bold text-white mb-1"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {userData?.xId !== "Not linked" ? `@${userData.xId}` : userData?.userId?.slice(0, 8) || "User"}
                </motion.h3>
                
                <motion.div 
                  className="flex items-center gap-1.5 mb-2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Crown className="w-3.5 h-3.5 text-[#FFD964]" />
                  <span className="text-[#FFD964] font-medium text-xs">{levelTitle}</span>
                </motion.div>
                
                {/* More Visible Member Since */}
                <motion.div
                  className="flex items-center gap-1.5 text-xs text-[#9FA6B2] font-medium"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Shield className="w-3 h-3" />
                  <span>Member since {formatDate(userData?.joinDate)}</span>
                </motion.div>
                
                {/* Total Points Display */}
                <motion.div
                  className="mt-2"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="text-2xl font-bold text-[#00FF9D]">
                    {formatNumber(userData?.points || 0)}
                  </div>
                  <div className="text-xs text-[#7C8694]">Total AmPoints</div>
                </motion.div>
              </div>
            </div>

            {/* Compact Top 3 Points Breakdown */}
            <div className="space-y-2 mb-4">
              <div className="text-xs text-[#7C8694] uppercase tracking-wider font-medium">Top Categories</div>
              {topCategories.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <motion.div
                    key={index}
                    className="relative overflow-hidden rounded-lg"
                    style={{
                      background: `linear-gradient(90deg, ${category.color}12 0%, transparent 100%)`,
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    whileHover={{ scale: 1.02, x: 4 }}
                  >
                    <div className="relative p-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div 
                          className="w-6 h-6 rounded-md flex items-center justify-center"
                          style={{ backgroundColor: `${category.color}20` }}
                        >
                          <IconComponent className="w-3.5 h-3.5" style={{ color: category.color }} />
                        </div>
                        <span className="text-xs font-medium text-white">{category.name}</span>
                      </div>
                      <div className="text-xs font-bold" style={{ color: category.color }}>
                        {formatNumber(category.points)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Enhanced Bottom Accent */}
            <motion.div 
              className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00FF9D] via-[#FFD964] to-[#3EBEFF] shadow-lg shadow-[#00FF9D]/30"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1.2, duration: 1.5 }}
            />
          </div>

          {/* Enhanced Holographic Overlay Effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                linear-gradient(110deg, 
                  transparent 35%, 
                  rgba(0, 255, 157, 0.05) 42%, 
                  rgba(255, 255, 255, 0.08) 50%, 
                  rgba(62, 190, 255, 0.05) 58%, 
                  transparent 65%
                )`,
            }}
            animate={{
              x: ["-120%", "220%"],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatDelay: 4,
              ease: "easeInOut",
            }}
          />

          {/* Enhanced Corner Accents */}
          <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#00FF9D]/40 rounded-tl-lg shadow-lg shadow-[#00FF9D]/20" />
          <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#3EBEFF]/40 rounded-tr-lg shadow-lg shadow-[#3EBEFF]/20" />
          <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#FFD964]/40 rounded-bl-lg shadow-lg shadow-[#FFD964]/20" />
          <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#00FF9D]/40 rounded-br-lg shadow-lg shadow-[#00FF9D]/20" />
        </motion.div>

        {/* Enhanced Floating Particles */}
        <div className="absolute -top-3 -right-3 w-2.5 h-2.5 bg-[#00FF9D] rounded-full animate-pulse shadow-lg shadow-[#00FF9D]/50" />
        <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-[#FFD964] rounded-full animate-pulse shadow-lg shadow-[#FFD964]/50" />
        <div className="absolute top-1/2 -right-2.5 w-2 h-2 bg-[#3EBEFF] rounded-full animate-pulse shadow-lg shadow-[#3EBEFF]/50" />
      </div>
    </motion.div>
  );
};

export default LiquidGlassPassport;